import React from 'react';
import { DeepReadonly } from 'utility-types';
import { IOrganization, OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { IUserOrganization } from '@pec/aion-ui-core/interfaces/userOrganization';
import { VF1CHASLogo } from './VF1CHASLogo';
import { VF1Logo } from './VF1Logo';
import { hasSubsidiaries } from '@pec/aion-ui-core/helpers/userOrganizations';
import { useUserPermission } from '../../hooks/useUserPermission';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';

const { ChasOrganizationFields, NewDashboard, ComplianceAUS, ComplianceUK, ComplianceUSA } = OrganizationFeature;
const { Manage } = ActivityAction;
const { Organizations } = ActivityResourceName;

type Props = {
  organizationId?: string;
  userOrganizations: DeepReadonly<IUserOrganization[]> | null;
  className?: string;
  organization: DeepReadonly<IOrganization> | null;
};

const Logo: React.FC<Props> = ({ userOrganizations, organizationId, className, organization }) => {
  const { hasGlobalPermission } = useUserPermission();
  const firstCHASOrganizationId = userOrganizations?.find(
    org => org.features.includes(ComplianceAUS) || org.features.includes(ComplianceUK)
  )?.id;

  const isVeriforceAdmin = hasGlobalPermission ? hasGlobalPermission(Manage, Organizations) : false;
  const hasNavigationToVeriforceOneEnabledOnAnyOrg = userOrganizations?.some(org =>
    org.features.includes(OrganizationFeature.CProNavigationToVeriforceOne)
  );

  const isCohortZeroEnabled = organization?.features.includes(OrganizationFeature.DovetailCohortZero);
  const hasNewClientDashboardEnabled = organization?.features.includes(OrganizationFeature.ClientDashboardNavigation);
  const connectLink = organizationId ? `/organizations/${organizationId}/connect` : '/';

  const isCHASOrganization = organization?.features.includes(ChasOrganizationFields);
  const isNewDashboard = organization?.features.includes(NewDashboard);

  const supplyChainsLink = `/organizations/${organizationId || firstCHASOrganizationId}/supply-chains-v2`;
  const clientDashboardLink = `/organizations/${organizationId}/client-dashboard`;
  const vf1Link = organizationId
    ? `/selectOrganization?organizationId=${organizationId}&returnTo=${encodeURIComponent('/?hint=vf1home')}`
    : '/?hint=vf1home';
  const newDashboardLink = `/organizations/${organizationId}`;

  const hasCHASCompliance = !!firstCHASOrganizationId;
  const hasUSACompliance = userOrganizations?.some(org => org.features.includes(ComplianceUSA));

  const getRedirectLink = () => {
    if (isVeriforceAdmin) {
      return '/links';
    }
    if (hasNavigationToVeriforceOneEnabledOnAnyOrg) {
      return '/?hint=homePostLogin';
    }

    if (isCohortZeroEnabled) {
      return connectLink;
    }
    if (!hasUSACompliance) {
      if (hasCHASCompliance) {
        if (hasNewClientDashboardEnabled && hasSubsidiaries(userOrganizations!, organizationId!)) {
          return clientDashboardLink;
        }
        return supplyChainsLink;
      }
      if (isCHASOrganization || (!isCHASOrganization && isNewDashboard)) {
        return newDashboardLink;
      }
    }
    return vf1Link;
  };

  return (
    <a href={getRedirectLink()} className={className}>
      {hasCHASCompliance || isCHASOrganization ? <VF1CHASLogo /> : <VF1Logo />}
    </a>
  );
};

export default Logo;
